import { Component, DestroyRef, ElementRef, ViewChild, computed, effect, inject, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { provideTranslocoScope, TranslocoModule } from '@jsverse/transloco';
import { AlertModule } from 'ngx-bootstrap/alert';
import { CommonFormValidationMessagesComponent } from '../../forms';
import {
  LoaderGlobeAnimationDetailComponent,
  LoadingObject,
  LoadingService,
} from '../../loaders';
import {
  CompletenessStatusCardRequirementItem,
  TrackingCompletenessStatusCardComponent,
} from '../../tracking/completeness-status-card';
import { UserLinksService } from '../../profile-user/user-links.service';
import { UserLinkViewModel, ProfessionalLinkType, ProfessionalCompleteness, ProfessionalLinkTemplate } from '../../profile-user/user-links.types';
import { AuthService } from '../../auth';
import { ButtonsModule } from "ngx-bootstrap/buttons";
import { ScrollService } from '../../utilities/scroll.service';

@Component({
  selector: 'multisite-professional-links',
  templateUrl: './professional-links.component.html',
  styleUrls: ['./professional-links.component.scss'],
  standalone: true,
  imports: [
      ReactiveFormsModule,
      TranslocoModule,
      AlertModule,
      LoaderGlobeAnimationDetailComponent,
      CommonFormValidationMessagesComponent,
      TrackingCompletenessStatusCardComponent,
      ButtonsModule
  ],
  providers: [
    provideTranslocoScope('profile'), 
  ]
})
export class ProfessionalLinksComponent {
  public readonly userLinksService = inject(UserLinksService);
  private readonly scrollService = inject(ScrollService);
  private readonly loadingService = inject(LoadingService);
  private readonly authService = inject(AuthService);
  private readonly destroyRef = inject(DestroyRef);
  @ViewChild('addLinkSection') private addLinkSection?: ElementRef<HTMLElement>;
  newLinkForm : FormGroup | undefined = undefined;
  selectedTemplateType = signal<ProfessionalLinkType | undefined>(undefined);
  readonly professionalLinks = signal<UserLinkViewModel[]>([]);
  readonly professionalCompleteness = signal<ProfessionalCompleteness>({
    hasLinkedin: false,
    hasOrganisation: false,
    hasNetwork: false,
    hasPublication: false,
    isComplete: false,
  });
  readonly submitAttempted = signal(false);
  readonly error = signal<string | null>(null);
  readonly editingLinkId = signal<number | null>(null);

  loadingObject = signal<LoadingObject>({});



  readonly editLinkForm = new FormGroup({
    url: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(5), Validators.maxLength(2000)],
    }),
    label: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.maxLength(255)],
    }),
    description: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.maxLength(500)],
    }),
  });

  readonly initialLoading = computed(() => this.professionalLinks() === undefined);
  readonly isAnythingLoading = computed<boolean>(() => {
    const requestInFlight = this.userLinksService.loadingObjectSignal()['userLinks'] === true;
    const createInFlight = this.userLinksService.loadingObjectSignal()['createLink'] === true;
    const updateInFlight = this.userLinksService.loadingObjectSignal()['updateLink'] === true;
    const deleteInFlight = this.userLinksService.loadingObjectSignal()['deleteLink'] === true;
    return (
      requestInFlight ||
      createInFlight ||
      updateInFlight ||
      deleteInFlight ||
      this.loadingService.isAnythingLoading(this.loadingObject())
    );
  });

  readonly formDisabled = computed(() => this.isAnythingLoading());

   hasRole (roleName:string){
    return this.authService.checkRole(roleName); // for example "Admin"
  }

  readonly linksByType = computed(() => {
    const links = this.professionalLinks();
    return {
      linkedin: links.filter((l) => l.type === 'linkedin'),
      organisation: links.filter((l) => l.type === 'organisation'),
      project: links.filter((l) => l.type === 'project'),
      network: links.filter((l) => l.type === 'network'),
      publication: links.filter((l) => l.type === 'publication'),
    };
  });


  selectedTemplate = computed<ProfessionalLinkTemplate | undefined>(() => {
    const typeValue = this.selectedTemplateType();
    if (!typeValue) {
      return undefined;
    }
    return this.userLinksService.professionalLinkTemplates().find((t) => t.type === typeValue);
  });
 


  readonly completenessRequirementItems = computed<CompletenessStatusCardRequirementItem[]>(() => {
    const completeness = this.professionalCompleteness();
    const hasProfessional = completeness.hasOrganisation || completeness.hasNetwork || completeness.hasPublication;

    return [
      {
        id: 'linkedin',
        met: completeness.hasLinkedin,
        translationKey: 'profile.interculturalist_status_requirement_linkedin',
        translationScope: undefined,
        text: null,
      },
      {
        id: 'professional',
        met: hasProfessional,
        translationKey: 'profile.interculturalist_status_requirement_professional_link',
        translationScope: undefined,
        text: null,
      },
    ];
  });

  constructor() {
   this.newLinkForm = new FormGroup({
    type: new FormControl<ProfessionalLinkType | ''>('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
    url: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(5), Validators.maxLength(2000)],
    }),
    label: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.maxLength(255)],
    }),
    description: new FormControl<string>('', {
      nonNullable: true,
      validators: [Validators.maxLength(500)],
    }),
  });
  this.newLinkForm.get('type')?.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
    this.selectedTemplateType.set(value || undefined);
  });
    effect(() => {
      const newLinkForm = this.newLinkForm;
      if(newLinkForm){
        if (this.formDisabled()) {
          newLinkForm.disable({ emitEvent: false });
          this.editLinkForm.disable({ emitEvent: false });
        } else {
          newLinkForm.enable({ emitEvent: false });
          this.editLinkForm.enable({ emitEvent: false });
        }
      }
    });
  }

  ngOnInit(): void {
    this.userLinksService.professionalLinks$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((links) => {
        this.professionalLinks.set(links);
      });

    this.userLinksService.professionalCompleteness$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((completeness) => {
        this.professionalCompleteness.set(completeness);
      });

    this.userLinksService.userLinks$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((allLinks) => {
        if (allLinks.length === 0) {
          this.requestInitialLinksIfNeeded();
        }
      });

    this.requestInitialLinksIfNeeded();
  }

  private requestInitialLinksIfNeeded(): void {
    const isLoading = this.userLinksService.loadingObjectSignal()['userLinks'] === true;
    const hasLinks = this.userLinksService.userLinksState.length > 0;
    if (!isLoading && !hasLinks) {
      this.userLinksService
        .fetchUserLinks()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          error: (errorResponse) => {
            this.error.set(
              typeof errorResponse === 'string'
                ? errorResponse
                : errorResponse?.message ?? 'multisite.something_went_wrong',
            );
          },
        });
    }
  }

  onSelectType(type: ProfessionalLinkType): void {
    if(this.newLinkForm){
      this.newLinkForm.patchValue({ type });
    }
    requestAnimationFrame(() => {
      const addLinkSection = this.addLinkSection?.nativeElement;
      if (addLinkSection) {
        this.scrollService.scrollToElement(addLinkSection, 500, 20);
      }
    });
  }

  onAddLink(): void {
    this.submitAttempted.set(true);
    if(!this.newLinkForm){
      return;
    }
    if (!this.newLinkForm.valid) {
      return;
    }

    
    const formValue = this.newLinkForm.getRawValue();
    if (formValue.type === '') {
      return;
    }
    const category = formValue.type === 'linkedin' ? 'social' : 'profile';

    this.userLinksService
      .createUserLink({
        category: category,
        type: formValue.type as ProfessionalLinkType,
        url: formValue.url,
        label: formValue.label || null,
        description: formValue.description || null,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          if(this.newLinkForm){

            this.newLinkForm.reset();
            this.submitAttempted.set(false);
            this.error.set(null);
          }
        },
        error: (errorMessage) => {
          this.error.set(errorMessage);
        },
      });
  }

  onEditLink(link: UserLinkViewModel): void {
    this.editingLinkId.set(link.id);
    this.editLinkForm.patchValue({
      url: link.url,
      label: link.label || '',
      description: link.description || '',
    });
  }

  onCancelEdit(): void {
    this.editingLinkId.set(null);
    this.editLinkForm.reset();
    this.submitAttempted.set(false);
  }

  onSaveEdit(): void {
    this.submitAttempted.set(true);
    const editingId = this.editingLinkId();
    if (!editingId || !this.editLinkForm.valid) {
      return;
    }

    const formValue = this.editLinkForm.getRawValue();
    const link = this.professionalLinks().find((l) => l.id === editingId);
    if (!link) {
      return;
    }

    this.userLinksService
      .updateUserLink(editingId, {
        category: link.category,
        type: link.type as ProfessionalLinkType,
        url: formValue.url,
        label: formValue.label || null,
        description: formValue.description || null,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.editingLinkId.set(null);
          this.editLinkForm.reset();
          this.submitAttempted.set(false);
          this.error.set(null);
        },
        error: (errorMessage) => {
          this.error.set(errorMessage);
        },
      });
  }

  onDeleteLink(id: number): void {
    if (!confirm('Are you sure you want to delete this link?')) {
      return;
    }

    this.userLinksService
      .deleteUserLink(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.error.set(null);
        },
        error: (errorMessage) => {
          this.error.set(errorMessage);
        },
      });
  }

  readonly isLoading = computed(() => this.initialLoading() || this.isAnythingLoading());
}
